import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Link,
  useFetcher,
  useNavigate,
  useNavigation,
  useRevalidator,
  useSearchParams,
} from 'react-router';
import { BookingDialog } from '~/ui/booking-dialog/booking-dialog';
import { FilterPicker } from '~/ui/filter-picker';
import * as tracker from '~/domain/plugins/services/tracker.client';
import { buildAddToCartPayload } from '~/domain/plugins/services/tracker/events';
import { availabilityTicketFilter, validateReservation } from '../utils';
import { ticketTrackingLines } from '../tracking';
import { ticketSelectionError } from '../ticket-selection-error';
import { useAwaited } from '~/lib/use-awaited';
import type { BookingMode } from '~/domain/availability/index.server';
import { useLocaleHref } from '~/lib/use-locale-href';
import { FLOW_STEP_PATHS } from '../checkout-links';
import type { BookingDialogLoaderData } from './load.server';

type BookingDialogRouteProps = {
  /** The loader payload: sync fields (experience id, selected date, active
   *  filters) plus the deferred `tickets` and `events` promises that drive
   *  per-section loading states. */
  loaderData: BookingDialogLoaderData;
  /** Where closing the dialog navigates to (the parent route). */
  closeTo: string;
  /** Keep the current scroll position when Continue submits. Set for the
   *  checkout edit flow, which redirects back to the info page the visitor was
   *  already scrolled on; left off for the experience flow, which opens a fresh
   *  checkout that should start at the top. */
  preserveScrollOnContinue?: boolean;
  /** How the page that opened the dialog offered the experience's dates. Sent
   *  with Continue so the info step can count its steps without reading the
   *  events again. */
  bookingMode?: BookingMode;
};

/**
 * Whether closing can safely pop the history stack. React Router increments
 * `history.state.idx` on each in-app navigation, so `idx > 0` means we
 * arrived here via an in-app push (the trigger Link) and popping returns to
 * the opener with no duplicate entry.
 *
 * A full document load (refresh, deep link, external referrer) resets `idx`
 * to 0. We intentionally treat that as "can't pop" and replace-with-parent
 * instead — popping there could send the visitor off-site (the back entry
 * may be an external page), which is worse than landing cleanly on the
 * parent route.
 */
function openedViaInAppPush(): boolean {
  if (typeof window === 'undefined') return false;
  return ((window.history.state as { idx?: number } | null)?.idx ?? 0) > 0;
}

/**
 * Wires the controlled `BookingDialog` to the route. The dialog (with a live
 * `DateStepper` on the loader's sync `selectedDate`) renders immediately;
 * the tickets field and event list show their own skeletons until the
 * corresponding deferred promise resolves (`useAwaited` keeps previous data
 * visible across revalidations, so in-dialog changes never flash a skeleton).
 * Filter changes go to URL search params (loader refetches), ticket
 * quantities keep local state for instant feedback while syncing to the URL,
 * and Continue posts to the action.
 */
export function BookingDialogRoute({
  loaderData,
  closeTo,
  preserveScrollOnContinue = false,
  bookingMode,
}: BookingDialogRouteProps) {
  const navigate = useNavigate();
  const localeHref = useLocaleHref();
  const fetcher = useFetcher();
  const navigation = useNavigation();
  const revalidator = useRevalidator();
  const [searchParams] = useSearchParams();

  const tickets = useAwaited(loaderData.tickets);
  const events = useAwaited(loaderData.events);

  // Continue spans two phases: the fetcher posts to the action
  // (`fetcher.state !== 'idle'`), then react-router follows the action's
  // redirect via a top-level navigation (`navigation.state !== 'idle'`).
  // We can't just OR them, because URL-param updates from filter / ticket
  // changes also trip top-level navigations — that'd flicker the button
  // busy during normal interaction. So we latch a local flag on click and
  // clear it once both signals settle.
  const [continueInFlight, setContinueInFlight] = useState(false);
  // React Router publishes the fetcher's `submitting` state in a transition,
  // so the render that sets the latch still sees both signals idle. Only an
  // idle that follows a busy fetcher means the continue has settled.
  const continueStarted = useRef(false);
  useEffect(() => {
    if (!continueInFlight) return;
    if (fetcher.state !== 'idle') {
      continueStarted.current = true;
      return;
    }
    if (continueStarted.current && navigation.state === 'idle') {
      continueStarted.current = false;
      setContinueInFlight(false);
    }
  }, [continueInFlight, fetcher.state, navigation.state]);

  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const loadedQuantities = tickets?.ticketQuantities;
  useEffect(() => {
    if (loadedQuantities) setQuantities(loadedQuantities);
  }, [loadedQuantities]);

  const [filterOpen, setFilterOpen] = useState(false);

  // FilterPicker reads `FilterOptions` ({ languages, locations }). Loader
  // returns the same axes as two flat arrays for fixture compatibility, so
  // we re-pack here.
  const filterOptions = useMemo(
    () =>
      events
        ? {
            languages: events.languageOptions,
            locations: events.locationOptions,
          }
        : null,
    [events]
  );
  const hasFilterableLanguages = (events?.languageOptions.length ?? 0) > 1;
  const hasFilterableLocations = (events?.locationOptions.length ?? 0) > 1;
  const showFilterButton = hasFilterableLanguages || hasFilterableLocations;
  const activeFilterCount =
    loaderData.activeLanguages.length + loaderData.activeLocations.length;

  function replaceParams(mutate: (params: URLSearchParams) => void) {
    const next = new URLSearchParams(searchParams);
    mutate(next);
    // In-dialog ticket/date changes must never scroll the page behind the
    // dialog — mirrors the filter picker and other in-place navigations.
    navigate(`?${next.toString()}`, {
      replace: true,
      preventScrollReset: true,
    });
  }

  function handleQuantities(next: Record<string, number>) {
    setQuantities(next);
    replaceParams((params) => {
      params.delete('t');
      const selected = Object.entries(next).filter(([, qty]) => qty > 0);
      for (const [id, qty] of selected) params.append('t', `${id}:${qty}`);
      // Keep an explicit (empty) `t` marker when every ticket is cleared, so
      // the loader treats it as a deliberate zero rather than a fresh open and
      // doesn't restore the basket's previous tickets.
      if (selected.length === 0) params.set('t', '');
    });
  }

  function handleClose() {
    if (openedViaInAppPush()) {
      navigate(-1);
    } else {
      navigate(closeTo, { replace: true });
    }
  }

  const ticketList = useMemo(
    () =>
      Object.entries(quantities).map(([variantId, quantity]) => ({
        variantId,
        quantity,
      })),
    [quantities]
  );

  // Availability filter for the DateStepper: below the reservation minimum the
  // ticket filter is omitted so an incomplete selection doesn't gray out the
  // calendar dots (matching the loader's event query). Undefined until the
  // ticket data resolves — the calendar starts with unfiltered availability.
  const availabilityTickets = useMemo(
    () =>
      tickets
        ? availabilityTicketFilter(
            ticketList,
            tickets.ticketVariants,
            tickets.reservationConfig
          )
        : undefined,
    [ticketList, tickets]
  );

  // Validate the ticket selection client-side (instant, matching the checkout
  // dates step) and render the same error copy under the TicketsField. `null`
  // when the selection is valid (or still loading), so Continue may proceed.
  const reservation = useMemo(
    () =>
      tickets
        ? validateReservation(
            ticketList,
            tickets.ticketVariants,
            tickets.reservationConfig
          )
        : null,
    [ticketList, tickets]
  );
  const hasTickets = ticketList.some((t) => t.quantity > 0);
  const ticketError =
    reservation && (!hasTickets || !reservation.ok)
      ? ticketSelectionError(true, hasTickets, reservation)
      : null;

  const dateByEvent = useMemo(() => {
    const map = new Map<string, string>();
    for (const group of events?.groups ?? []) {
      for (const event of group.events) map.set(event.eventId, group.date);
    }
    return map;
  }, [events?.groups]);

  function handleContinue(eventId: string) {
    const date = dateByEvent.get(eventId);
    if (!date) return;
    // Tickets picked and the visitor is moving on to booking info — the
    // funnel's `add_to_cart` moment (US-11215). This is the path a visitor
    // takes from an experience details page, so it has to push the same event
    // as the in-checkout time steps.
    if (tickets) {
      const tracking = ticketTrackingLines(ticketList, tickets.ticketVariants);
      if (tracking.currency) {
        tracker.event(
          'add_to_cart',
          buildAddToCartPayload({
            experienceId: loaderData.experienceId,
            experienceName: tickets.experienceName,
            currency: tracking.currency,
            lines: tracking.lines,
            experienceEventId: eventId,
          })
        );
      }
    }
    setContinueInFlight(true);
    fetcher.submit(
      {
        intent: 'continue',
        eventId,
        date,
        tickets: JSON.stringify(ticketList),
        ...(bookingMode ? { bookingMode } : {}),
      },
      { method: 'post', preventScrollReset: preserveScrollOnContinue }
    );
  }

  return (
    <>
      <BookingDialog
        ticketVariants={tickets?.ticketVariants ?? null}
        ticketQuantities={quantities}
        onTicketQuantitiesChange={handleQuantities}
        fromDate={loaderData.selectedDate}
        onFromDateChange={(date) => replaceParams((p) => p.set('from', date))}
        experienceId={loaderData.experienceId}
        filters={{
          languages: loaderData.activeLanguages,
          locationIds: loaderData.activeLocations,
          tickets: availabilityTickets,
        }}
        initialFocusDate={loaderData.selectedDate}
        showFilterButton={showFilterButton}
        activeFilterCount={activeFilterCount}
        onOpenFilters={() => setFilterOpen(true)}
        seatStep={Math.max(1, tickets?.reservationConfig.multipleOf ?? 1)}
        maxSeats={tickets?.reservationConfig.maxReservationSeats}
        seatCounterVisibility={loaderData.seatCounterVisibility}
        ticketError={ticketError}
        groups={events?.groups ?? null}
        eventsUnavailable={
          events?.availabilityUnavailable
            ? {
                onRetry: () => void revalidator.revalidate(),
                retrying: revalidator.state === 'loading',
              }
            : null
        }
        defaultSelectedEventId={events?.defaultEventId ?? null}
        onContinue={handleContinue}
        continueBusy={continueInFlight}
        onClose={handleClose}
      />
      {/* Continue redirects to the info step. A link to it, rendered while the
          visitor picks, has React Router discover that route now instead of
          fetching its manifest between the Continue post and the step's data. */}
      <Link
        to={localeHref(FLOW_STEP_PATHS.scheduled.info, {
          experienceId: loaderData.experienceId,
        })}
        discover="render"
        hidden
        tabIndex={-1}
        aria-hidden
      />
      {filterOpen && filterOptions && (
        <FilterPicker
          // Opens over the booking dialog, so it layers rather than blurring
          // an already-blurred backdrop a second time.
          stacked
          filterOptions={filterOptions}
          activeLanguages={loaderData.activeLanguages}
          activeLocations={loaderData.activeLocations}
          onClose={() => setFilterOpen(false)}
        />
      )}
    </>
  );
}
